import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Download, FileSpreadsheet, FileText, RefreshCw, File, FileJson,
  Briefcase, Layers, TrendingUp, AlertTriangle, Filter, CheckCircle2,
  Info, BarChart3, Receipt, Users,
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

applyPlugin(jsPDF);

const COLORS = [
  "#0ea5e9", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444",
  "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16",
];

const ANALISIS_BG = {
  warning: "bg-amber-50 border-amber-200 text-amber-900",
  info: "bg-blue-50 border-blue-200 text-blue-900",
  success: "bg-emerald-50 border-emerald-200 text-emerald-900",
};

const ANALISIS_ICON = {
  warning: AlertTriangle, info: Info, success: CheckCircle2,
};


const RecaudacionActividadEspecifica = () => {
  const { API, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    actividad: "", fecha_desde: "", fecha_hasta: "",
    estatus: "todos", solo_con_recaudacion: false,
  });
  const [tableSearch, setTableSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.actividad.trim()) params.actividad = filters.actividad.trim();
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      params.estatus = filters.estatus;
      params.solo_con_recaudacion = filters.solo_con_recaudacion;
      const r = await axios.get(`${API}/reportes/recaudacion-actividad-especifica`, { params, headers });
      setData(r.data);
    } catch (err) {
      toast.error("No se pudo cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filters.estatus, filters.solo_con_recaudacion]);

  const onApplyFilters = () => fetchData();
  const onResetFilters = () => {
    setFilters({ actividad: "", fecha_desde: "", fecha_hasta: "", estatus: "todos", solo_con_recaudacion: false });
    setTableSearch("");
  };

  const filteredFilas = useMemo(() => {
    const filas = data?.filas || [];
    if (!tableSearch.trim()) return filas;
    const q = tableSearch.toLowerCase();
    return filas.filter((f) => f.actividad.toLowerCase().includes(q));
  }, [data, tableSearch]);

  const exportRows = () =>
    filteredFilas.map((f) => ({
      "Actividad Específica": f.actividad,
      "Contribuyentes Asignados": f.contribuyentes_asignados,
      "Con Recaudación": f.contribuyentes_con_recaudacion,
      "Comprobantes": f.comprobantes_count,
      "Aprobados": f.comprobantes_aprobados,
      "Total Recaudado (Bs)": f.total_recaudado_aprobado,
      "Total Anulado (Bs)": f.total_anulado,
      "Promedio (Bs)": f.promedio_aprobado,
    }));

  const exportToExcel = () => {
    const rows = exportRows();
    if (!rows.length) { toast.error("Sin datos"); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recaudación");
    XLSX.writeFile(wb, `recaudacion_actividad_especifica_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportToPDF = () => {
    const rows = exportRows();
    if (!rows.length) { toast.error("Sin datos"); return; }
    const doc = new jsPDF("landscape");
    doc.setFontSize(16); doc.text("Reporte — Recaudación por Actividad Específica (ConLAE)", 14, 18);
    doc.setFontSize(10); doc.text(`Fecha: ${new Date().toLocaleDateString("es-VE")} · ${rows.length} actividades`, 14, 26);
    if (data?.resumen) {
      const r = data.resumen;
      doc.setFontSize(9);
      doc.text(
        `Total Aprobado: Bs ${(r.total_recaudado_aprobado || 0).toLocaleString("es-VE")}  ·  Comprobantes: ${r.comprobantes_aprobados}  ·  Contribuyentes: ${r.contribuyentes_con_actividad_especifica}/${r.total_contribuyentes_lae}`,
        14, 32
      );
    }
    doc.autoTable({
      head: [Object.keys(rows[0])],
      body: rows.map((r) => Object.values(r)),
      startY: 38, styles: { fontSize: 7 }, headStyles: { fillColor: [29, 78, 216] },
    });
    doc.save(`recaudacion_actividad_especifica_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportToCSV = () => {
    const rows = exportRows();
    if (!rows.length) { toast.error("Sin datos"); return; }
    const h = Object.keys(rows[0]).join(",");
    const body = rows.map((r) => Object.values(r).map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    saveAs(new Blob(["\ufeff" + `${h}\n${body}`], { type: "text/csv;charset=utf-8" }),
           `recaudacion_actividad_especifica_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const exportToJSON = () => {
    if (!data) { toast.error("Sin datos"); return; }
    saveAs(new Blob([JSON.stringify({ ...data, filas: filteredFilas }, null, 2)], { type: "application/json" }),
           `recaudacion_actividad_especifica_${new Date().toISOString().split("T")[0]}.json`);
    toast.success("Exportado a JSON");
  };

  const r = data?.resumen || {};
  const fmtNum = (n) => (n ?? 0).toLocaleString("es-VE");
  const fmtMoney = (n) => `Bs ${(n ?? 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="reporte-rec-act-esp-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center shadow-md">
            <Briefcase className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Recaudación por Actividad Económica Específica
            </h1>
            <p className="text-sm text-slate-500">
              Dashboard de contribuyentes con LAE clasificados por giro comercial (panadería, restaurante, etc.)
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="btn-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="btn-export">
                <Download className="w-4 h-4 mr-2" /> Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToPDF}><FileText className="w-4 h-4 mr-2 text-red-600" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToExcel}><FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV}><File className="w-4 h-4 mr-2 text-slate-600" /> CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToJSON}><FileJson className="w-4 h-4 mr-2 text-blue-600" /> JSON</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-5 pb-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <Filter className="w-4 h-4" /> Filtros
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Actividad</Label>
              <Input
                placeholder="Ej: panadería..."
                value={filters.actividad}
                onChange={(e) => setFilters({ ...filters, actividad: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && onApplyFilters()}
                data-testid="input-filtro-actividad"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Desde</Label>
              <Input type="date" value={filters.fecha_desde}
                     onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                     data-testid="input-desde" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Hasta</Label>
              <Input type="date" value={filters.fecha_hasta}
                     onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                     data-testid="input-hasta" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Estatus</Label>
              <Select value={filters.estatus} onValueChange={(v) => setFilters({ ...filters, estatus: v })}>
                <SelectTrigger className="w-[140px]" data-testid="select-estatus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="Aprobado">Aprobado</SelectItem>
                  <SelectItem value="Anulado">Anulado</SelectItem>
                  <SelectItem value="Activo">Activo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pb-2">
              <input type="checkbox" checked={filters.solo_con_recaudacion}
                     onChange={(e) => setFilters({ ...filters, solo_con_recaudacion: e.target.checked })}
                     className="w-4 h-4 accent-purple-700" data-testid="check-solo-rec" />
              Sólo con recaudación
            </label>
            <Button onClick={onApplyFilters} className="bg-purple-700 text-white" data-testid="btn-aplicar">Aplicar</Button>
            <Button variant="outline" onClick={onResetFilters} data-testid="btn-reset">Limpiar</Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard label="Total Recaudado" value={fmtMoney(r.total_recaudado_aprobado)} icon={Receipt} color="emerald" testid="kpi-total" />
        <KPICard label="Comprobantes OK" value={fmtNum(r.comprobantes_aprobados)} icon={CheckCircle2} color="blue" testid="kpi-comp-ok" />
        <KPICard label="Anulados" value={fmtNum(r.comprobantes_anulados)} icon={AlertTriangle} color="rose" testid="kpi-anulados" />
        <KPICard label="Contribuyentes" value={`${fmtNum(r.contribuyentes_con_actividad_especifica)}/${fmtNum(r.total_contribuyentes_lae)}`} icon={Users} color="indigo" testid="kpi-contribuyentes" />
        <KPICard label="Actividades en Uso" value={fmtNum(r.total_actividades_en_uso)} icon={Layers} color="purple" testid="kpi-act-uso" />
        <KPICard label="Promedio" value={fmtMoney(r.promedio_por_comprobante)} icon={TrendingUp} color="amber" testid="kpi-promedio" />
      </div>

      {/* Análisis */}
      {data?.analisis?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.analisis.map((a, i) => {
            const Icon = ANALISIS_ICON[a.tipo] || Info;
            return (
              <div key={i} className={`flex gap-3 p-3 rounded-lg border ${ANALISIS_BG[a.tipo] || ANALISIS_BG.info}`}>
                <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm leading-snug">{a.texto}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="chart-barras">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-600" /> Top 10 — Recaudación por actividad
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.grafico_barras || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data.grafico_barras} margin={{ top: 8, right: 16, bottom: 60, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="actividad" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `Bs ${(v || 0).toLocaleString("es-VE")}`} />
                  <Bar dataKey="total" fill="#7c3aed" radius={[4, 4, 0, 0]}>
                    {data.grafico_barras.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart label="Sin datos de recaudación" />}
          </CardContent>
        </Card>

        <Card data-testid="chart-pie">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-600" /> Distribución del monto recaudado
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.grafico_pie || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={data.grafico_pie} cx="50%" cy="50%" outerRadius={110}
                       dataKey="valor" nameKey="actividad"
                       label={({ actividad, valor }) => `${(actividad || "").slice(0, 18)}: Bs ${(valor || 0).toLocaleString("es-VE")}`}>
                    {data.grafico_pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `Bs ${(v || 0).toLocaleString("es-VE")}`} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyChart label="Sin datos" />}
          </CardContent>
        </Card>

        <Card data-testid="chart-contribuyentes">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-600" /> Contribuyentes por actividad (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.grafico_contribuyentes || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data.grafico_contribuyentes} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="actividad" tick={{ fontSize: 10 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="contribuyentes" fill="#4f46e5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart label="Sin contribuyentes asignados a actividades" />}
          </CardContent>
        </Card>

        <Card data-testid="chart-donut">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Estatus de comprobantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.grafico_donut_estatus || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={data.grafico_donut_estatus} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                       dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`}>
                    {data.grafico_donut_estatus.map((row, i) => <Cell key={i} fill={row.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyChart label="Sin comprobantes" />}
          </CardContent>
        </Card>

        {data?.grafico_lineas_mes?.length > 0 && (
          <Card data-testid="chart-lineas" className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-pink-600" /> Tendencia mensual de recaudación
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.grafico_lineas_mes} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `Bs ${(v || 0).toLocaleString("es-VE")}`} />
                  <Line type="monotone" dataKey="total" stroke="#ec4899" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabla detalle */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Detalle por actividad económica específica</CardTitle>
            <div className="flex items-center gap-3">
              <Input placeholder="Buscar en tabla..." className="w-[220px] h-8 text-sm"
                     value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} />
              <span className="text-xs text-slate-500">{filteredFilas.length} de {data?.filas?.length || 0}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-purple-600" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <th className="px-3 py-3 text-left">Actividad</th>
                    <th className="px-3 py-3 text-right">Contribuyentes</th>
                    <th className="px-3 py-3 text-right">Con Recaud.</th>
                    <th className="px-3 py-3 text-right">Comprob.</th>
                    <th className="px-3 py-3 text-right">Aprobados</th>
                    <th className="px-3 py-3 text-right">Total Recaudado</th>
                    <th className="px-3 py-3 text-right">Anulado</th>
                    <th className="px-3 py-3 text-right">Promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFilas.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Sin resultados</td></tr>
                  ) : filteredFilas.map((f, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50/60" data-testid={`row-${idx}`}>
                      <td className="px-3 py-2">
                        <span className="font-medium text-slate-800">{f.actividad}</span>
                        {f.es_custom && <span className="ml-2 text-[9px] uppercase text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">custom</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={`font-bold ${f.contribuyentes_asignados > 0 ? "text-indigo-700" : "text-slate-300"}`}>
                          {f.contribuyentes_asignados}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600">{f.contribuyentes_con_recaudacion}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{f.comprobantes_count}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{f.comprobantes_aprobados}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-bold">
                        {f.total_recaudado_aprobado.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right text-red-600">
                        {f.total_anulado.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {f.promedio_aprobado.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filteredFilas.length > 0 && (
                  <tfoot>
                    <tr className="bg-purple-50 font-semibold border-t-2 border-purple-200">
                      <td className="px-3 py-3 text-purple-900">Totales:</td>
                      <td className="px-3 py-3 text-right text-purple-900">
                        {filteredFilas.reduce((a, f) => a + f.contribuyentes_asignados, 0)}
                      </td>
                      <td className="px-3 py-3 text-right text-purple-900">
                        {filteredFilas.reduce((a, f) => a + f.contribuyentes_con_recaudacion, 0)}
                      </td>
                      <td className="px-3 py-3 text-right text-purple-900">
                        {filteredFilas.reduce((a, f) => a + f.comprobantes_count, 0)}
                      </td>
                      <td className="px-3 py-3 text-right text-purple-900">
                        {filteredFilas.reduce((a, f) => a + f.comprobantes_aprobados, 0)}
                      </td>
                      <td className="px-3 py-3 text-right text-purple-900">
                        Bs {filteredFilas.reduce((a, f) => a + f.total_recaudado_aprobado, 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-3 text-right text-purple-900">
                        Bs {filteredFilas.reduce((a, f) => a + f.total_anulado, 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


const KPICard = ({ label, value, icon: Icon, color, testid }) => {
  const colors = {
    blue: { border: "border-l-blue-600", icon: "text-blue-600", bg: "bg-blue-50" },
    emerald: { border: "border-l-emerald-600", icon: "text-emerald-600", bg: "bg-emerald-50" },
    amber: { border: "border-l-amber-500", icon: "text-amber-600", bg: "bg-amber-50" },
    indigo: { border: "border-l-indigo-600", icon: "text-indigo-600", bg: "bg-indigo-50" },
    purple: { border: "border-l-purple-600", icon: "text-purple-600", bg: "bg-purple-50" },
    rose: { border: "border-l-rose-600", icon: "text-rose-600", bg: "bg-rose-50" },
  }[color] || { border: "border-l-slate-500", icon: "text-slate-600", bg: "bg-slate-50" };
  return (
    <Card className={`border-l-4 ${colors.border}`} data-testid={testid}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium truncate">{label}</p>
            <p className="text-base font-bold text-slate-900 mt-1 truncate">{value}</p>
          </div>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors.bg}`}>
            <Icon className={`w-4 h-4 ${colors.icon}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EmptyChart = ({ label }) => (
  <div className="h-64 flex items-center justify-center text-slate-400 text-sm">{label}</div>
);


export default RecaudacionActividadEspecifica;
